// classify-agent Stage B: turn the agent's changes into ONE edit (one undo step).
// Pure: no React, no I/O. The user's own edits always win (EXPERIENCE.md § Component Patterns).
import { reclassifiedLayerName } from './utils.js'

// What the user has touched right now: remapped layers, overridden or protected handles.
// Computed at Apply time, so edits made while the agent ran are protected too.
export function userTouched(edit) {
  return {
    layers: new Set(Object.keys(edit.layerRemaps ?? {})),
    handles: new Set([...Object.keys(edit.handleClassOverrides ?? {}), ...Object.keys(edit.protectedLayers ?? {})]),
  }
}

// Apply the ops in order (later wins) -> { edit, applied, skipped, sublayers, expected }.
// `expected` maps handle -> class for the read-back check after the caller's pushEdit.
export function applyAgentResult(edit, result, accepted, touched = userTouched(edit)) {
  const layerRemaps = { ...edit.layerRemaps }
  const handleClassOverrides = { ...edit.handleClassOverrides }
  const applied = [], skipped = [], sublayers = new Set(), expected = {}
  for (const op of result.ops ?? []) {
    if (!accepted.has(op.op_id)) continue
    if (touched.layers.has(op.layer)) { skipped.push({ op, reason: 'your edit kept', count: op.count }); continue }
    if (op.kind === 'classify_layer') {
      layerRemaps[op.layer] = op.to
      // a whole-layer move clears earlier handle-level moves on that layer (overrides outrank remaps)
      for (const h of op.handles ?? []) {
        if (!touched.handles.has(h)) { delete handleClassOverrides[h]; expected[h] = op.to }
      }
      applied.push(op)
      continue
    }
    const free = (op.handles ?? []).filter(h => !touched.handles.has(h))
    if (free.length < (op.handles ?? []).length)
      skipped.push({ op, reason: op.handles.some(h => edit.protectedLayers?.[h]) ? 'protected' : 'your edit kept',
                     count: op.handles.length - free.length })
    if (!free.length) continue
    for (const h of free) { handleClassOverrides[h] = op.to; expected[h] = op.to }
    sublayers.add(reclassifiedLayerName(op.layer, op.to))
    applied.push({ ...op, count: free.length })
  }
  return { edit: { ...edit, layerRemaps, handleClassOverrides }, applied, skipped, sublayers: [...sublayers], expected }
}

// A tag question answered with a class: its per-layer ops as proposals to apply at once.
export function answerOps(question, choice) {
  return (question.ops ?? []).map(o => ({ ...o, to: choice, count: o.handles.length, why: 'You tagged them' }))
}

// Handles of a change the dropdown may still re-class: not protected, and not overridden by the user since
// (an override equal to the agent's own class is the agent's).
export function freeChangeHandles(edit, change) {
  if (edit.layerRemaps?.[change.layer]) return []
  return (change.handles ?? []).filter(h => !edit.protectedLayers?.[h]
    && (!(h in (edit.handleClassOverrides ?? {})) || edit.handleClassOverrides[h] === change.to))
}

// Undo an applied batch: allowed only while the current edit is still the batch's last one (compared by identity).
// -> { edit, hist } to restore, or null.
export function undoBatch(hist, current, meta) {
  const i = hist.lastIndexOf(meta?.base)
  return meta && current === meta.top && i >= 0 ? { edit: meta.base, hist: hist.slice(0, i) } : null
}
